import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../api";

const Card = ({ label, value, gold, red, testId }) => (
    <div className="rounded-2xl border border-lakou-gold/15 bg-lakou-coal/60 p-4" data-testid={testId}>
        <div className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-lakou-sand">{label}</div>
        <div className={`mt-1.5 font-display font-black text-xl ${gold ? "text-goldgrad" : red ? "text-red-400" : "text-lakou-cream"}`}>{value}</div>
    </div>
);

export const ControlFinance = () => {
    const [d, setD] = useState(null);
    const [form, setForm] = useState({ label: "", amount: "", category: "general" });

    const load = () => api.get("/control/finance").then((r) => setD(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const addExpense = async (e) => {
        e.preventDefault();
        try {
            await api.post("/control/expenses", { label: form.label, amount: parseFloat(form.amount), category: form.category });
            setForm({ label: "", amount: "", category: "general" });
            toast.success("Expense added");
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const delExpense = async (id) => {
        try {
            await api.delete(`/control/expenses/${id}`);
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    if (!d) return <p className="text-lakou-sand text-sm" data-testid="finance-loading">Loading…</p>;

    return (
        <div className="space-y-6" data-testid="control-finance">
            <h1 className="font-display font-black text-xl">Financial Management</h1>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Card label="Gross revenue" value={`$${d.revenue.toFixed(2)}`} gold testId="fin-gross" />
                <Card label="Stripe fees" value={`-$${d.stripe_fees.toFixed(2)}`} red testId="fin-fees" />
                <Card label="Net revenue" value={`$${d.net_revenue.toFixed(2)}`} testId="fin-net" />
                <Card label="Expenses" value={`-$${d.expenses_total.toFixed(2)}`} red testId="fin-expenses" />
                <Card label="Refunds" value={`-$${d.refunds_total.toFixed(2)}`} red testId="fin-refunds" />
                <Card label="NET PROFIT" value={`$${d.net_profit.toFixed(2)}`} gold testId="fin-profit" />
            </div>
            <div className="grid grid-cols-3 gap-3">
                <Card label="Stripe" value={`$${d.revenue_card.toFixed(2)}`} testId="fin-card" />
                <Card label="MonCash" value={`$${d.revenue_moncash.toFixed(2)}`} testId="fin-moncash" />
                <Card label="NatCash" value={`$${d.revenue_natcash.toFixed(2)}`} testId="fin-natcash" />
            </div>

            <section className="rounded-2xl border border-lakou-gold/15 bg-lakou-coal/60 p-5 space-y-4">
                <h2 className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-gold">Expenses</h2>
                <form onSubmit={addExpense} className="grid grid-cols-2 gap-2" data-testid="expense-form">
                    <input required placeholder="Label (e.g. Sound system)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="expense-label"
                        className="col-span-2 sm:col-span-1 rounded-xl border border-lakou-gold/20 bg-lakou-raise/70 px-4 py-3 text-sm text-lakou-cream placeholder:text-lakou-sand focus:outline-none focus:border-lakou-gold" />
                    <div className="flex gap-2">
                        <input required type="number" step="0.01" min="0.01" placeholder="Amount $" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount"
                            className="flex-1 rounded-xl border border-lakou-gold/20 bg-lakou-raise/70 px-4 py-3 text-sm text-lakou-cream placeholder:text-lakou-sand focus:outline-none focus:border-lakou-gold" />
                        <button type="submit" data-testid="expense-add"
                            className="rounded-xl bg-lakou-gold text-lakou-ink px-4 font-display font-bold text-xs uppercase tracking-wider inline-flex items-center gap-1.5">
                            <Plus size={14} /> Add
                        </button>
                    </div>
                </form>
                <div className="space-y-2" data-testid="expense-list">
                    {d.expenses.map((e) => (
                        <div key={e.id} className="flex items-center justify-between rounded-xl bg-lakou-raise/60 px-4 py-2.5">
                            <span className="text-sm text-lakou-cream">{e.label} <span className="font-mono2 text-[9px] text-lakou-sand uppercase">{e.category}</span></span>
                            <span className="flex items-center gap-3">
                                <span className="font-mono2 text-xs text-red-400">-${e.amount.toFixed(2)}</span>
                                <button onClick={() => delExpense(e.id)} data-testid={`expense-del-${e.id.slice(0, 6)}`} className="text-lakou-sand hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                            </span>
                        </div>
                    ))}
                    {!d.expenses.length && <p className="text-xs text-lakou-sand">No expenses recorded.</p>}
                </div>
            </section>

            <section className="rounded-2xl border border-lakou-gold/15 bg-lakou-coal/60 p-5">
                <h2 className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-gold mb-3">Recent transactions</h2>
                <div className="space-y-2" data-testid="finance-tx">
                    {d.transactions.slice(0, 30).map((t) => (
                        <div key={t.session_id} className="flex items-center justify-between rounded-xl bg-lakou-raise/60 px-4 py-2.5">
                            <div className="min-w-0">
                                <span className="font-mono2 text-[10px] text-lakou-cream">{(t.session_id || "").slice(0, 18)}…</span>
                                <span className="ml-2 font-mono2 text-[9px] uppercase text-lakou-sand">{t.order_type} · {t.payment_status}</span>
                            </div>
                            <span className="font-mono2 text-xs text-lakou-gold shrink-0">${t.amount?.toFixed(2)}</span>
                        </div>
                    ))}
                    {!d.transactions.length && <p className="text-xs text-lakou-sand">No transactions yet.</p>}
                </div>
            </section>
        </div>
    );
};
